// The CARTO basemap, in one place - both the full map page and the per-device
// check-ins mini-map draw from here.
//
// CARTO stopped serving keyless tiles from basemaps.cartocdn.com in September
// 2026: without a key the tiles still load but come back stamped "API KEY
// REQUIRED". The key is a free, no-account one from
// https://carto.com/basemaps/apikey and goes on every cartocdn URL as ?key=.
//
// Note what this key is NOT: a secret. This is a static SPA, so whatever the
// build is given ends up in the shipped bundle and in every browser's network
// tab - unavoidable for a basemap the browser fetches directly. It's kept out
// of git and injected at build time (see the deploy-portal workflow) so it can
// be rotated without a code change; the thing that actually stops someone else
// burning the quota is CARTO's domain allowlist, which scopes the prod key to
// tracker.sieting.family. That's referrer-matched, so the prod key won't work
// from a localhost dev server - see the portal README.
import { CARTO_API_KEY } from '../config';

export const BASEMAP_ATTRIBUTION = '&copy; OpenStreetMap contributors &copy; CARTO';

/**
 * Deepest zoom CARTO actually has raster tiles for. Past this the basemap
 * went blank: Leaflet kept requesting tiles that don't exist, so there was
 * nothing to draw under the markers.
 *
 * Paired with maxZoom below, this is what stops that. `maxNativeZoom` tells
 * Leaflet to stop requesting new tiles here and upscale these instead, so
 * zooming to maxZoom keeps a (softer) basemap rather than losing it. The
 * markers and trail are canvas overlays, not tiles, so they stay sharp at
 * every zoom - only the backdrop softens, which is the right thing to trade.
 *
 * 18 is deliberately conservative. CARTO's own Leaflet example sets
 * maxZoom: 20 while third-party tile catalogues list light_all as native to
 * 18, and the keyless endpoint returns an identical "API KEY REQUIRED" stub
 * at every zoom (even z22), so it can't be probed without a live key. If
 * z19-20 look softer than you'd like, raise this one number until the
 * basemap goes blank again, then back off by one.
 */
const MAX_NATIVE_ZOOM = 18;

/** Leaflet tileLayer options shared by every basemap layer in the portal. */
export const BASEMAP_TILE_OPTIONS = {
  subdomains: 'abcd',
  maxZoom: 20,
  maxNativeZoom: MAX_NATIVE_ZOOM,
} as const;

/**
 * Tile URL template for the given theme ('light' | anything else -> dark),
 * with the CARTO key appended when one was configured at build time.
 */
export function basemapTileUrl(theme: string): string {
  const style = theme === 'light' ? 'light_all' : 'dark_all';
  const url = `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png`;
  return CARTO_API_KEY ? `${url}?key=${encodeURIComponent(CARTO_API_KEY)}` : url;
}
